<template>
  <div class="page">
    <div class="columns is-centered">
      <div class="column is-one-third mx-3">
        <h3 class="pt-4 pb-2 is-size-5" v-if="userFullname">
          {{ userFullname }}
        </h3>
        <h1
          :class="{
            'pb-3': userFullname,
            'py-3': !userFullname,
          }"
          class="pr-5"
        >
          {{ $t("page.headers.bikenta_welcome") }}
        </h1>
        <p class="welcome-description is-size-6 pr-5">
          {{ $t("page.headers.welcome_description") }}
        </p>
        <ul class="attachments-list mt-2 pl-4 is-size-6">
          <li>
            <a href="./docs/manual-miembro.pdf" download>
              {{ $t("page-home.members-doc") }}
            </a>
          </li>
        </ul>
        <hr />
        <h2 class="my-4 is-size-4">
          <span class="has-text-primary">{{ $t("page.content.has_count") }}</span>
          {{ count }} {{ $t("page.content.parcels") }}
        </h2>
        <div class="background-gray py-3 px-2">
          <div class="columns is-mobile align-items-flex-end">
            <div class="column is-4">
              <img src="@/assets/icon-tree-big.png" />
            </div>
            <div class="column">
              <span class="has-text-weight-semibold is-size-4">
                <span class="has-text-primary">
                  {{ count }} {{ $t("page.content.parcels") }}
                </span>
                de
                <span class="has-text-primary nowrap">{{ totalHA }} ha</span>
              </span>
            </div>
          </div>
          <router-link
            class="mt-3 button is-link is-inverted is-fullwidth"
            :to="{ name: 'parcels' }"
          >
            {{ $t("page.labels.show_parcels") }}
          </router-link>
        </div>

        <card
          css-class="certification-card has-background-warning"
          v-if="person && isManager && (displayFSC || displayPEFC)"
        >
          <img slot="image" src="@/assets/certification.png" />
          <div slot="header">
            <label class="has-text-uppercase">
              {{ $t("page.headers.certifications") }}:
            </label>
            <div class="certification-display" v-if="displayFSC">
              <span class="name">FSC&#174;</span>
              <span class="code">{{ person.fsc_code }}</span>
              <div class="mb-4">
                <router-link :to="{ name: 'declaration', params: { type: 'FSC' } }">
                  {{ $t("page.headers.show_declaration") }}
                </router-link>
              </div>
            </div>
            <div class="certification-display" v-if="displayPEFC">
              <span class="name">PEFC&trade;</span>
              <span class="code">{{ person.pefc_code }}</span>
              <div>
                <router-link :to="{ name: 'declaration', params: { type: 'PEFC' } }">
                  {{ $t("page.headers.show_declaration") }}
                </router-link>
              </div>
            </div>
          </div>
        </card>
        <div v-if="attachments.length > 0">
          <h2 class="has-text-uppercase has-text-primary mt-5">
            {{ $t("page.headers.documentation") }}:
          </h2>
          <div class="py-3 px-2">
            <ul class="mt-2">
              <li
                v-for="attachment in attachments"
                :key="attachment.id"
                class="is-flex"
              >
                <article class="collapsible-article">
                  <header class="header background-gray mb-3">
                    <div
                      class="article-icon has-background-white has-text-centered is-size-1 has-text-grey ico-incendios"
                    ></div>
                    <h1 class="py-2 pl-2 pr-1">{{ attachment.name }}</h1>
                    <a @click.prevent="downloadAttachment(attachment.download_url)">
                      <div class="arrow-download"></div>
                    </a>
                  </header>
                </article>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="column is-one-third mx-1">
        <line-chart title="Histórico CO2" :chart-data="co2GlobalRecords"></line-chart>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import Vue from "vue"
import { mapGetters } from "vuex"
import Card from "@/components/Card"
import LineChart from "@/components/LineChart"

import * as _ from "lodash"

export default {
  name: "home",
  components: { Card, LineChart },
  mounted() {
    this.getPerson()
    this.getParcels()
    this.getAttachments()
    this.getDataChart()
  },
  data: () => ({
    count: 0,
    parcels: [],
    attachments: [],
    person: {},
    co2GlobalRecords: {},
  }),
  computed: {
    ...mapGetters(["userFullname"]),
    totalHA() {
      const totalArea = this.$_.sumBy(this.parcels, (parcel) => {
        return parcel.parcel.area
      })
      return Vue.filter("areaToHA")(totalArea)
    },
  },
  methods: {
    isManager() {
      return this.person && this.person.person_type === "manager"
    },
    displayFSC() {
      const conditions = [
        this.person.fsc_code,
        this.$_.contains(["FSC", "DOUBLE"], this.parcel.certification_type),
      ]
      return this.$_.every(conditions)
    },
    displayPEFC() {
      const conditions = [
        this.person.pefc_code,
        this.$_.contains(["PEFC", "DOUBLE"], this.parcel.certification_type),
      ]
      return this.$_.every(conditions)
    },
    getPerson() {
      api.getPerson().then((response) => {
        this.person = response.data
      })
    },
    getParcels() {
      api.getParcels(10000).then((response) => {
        this.parcels = response.data.results
        this.count = response.data.count
      })
    },
    getAttachments() {
      api.getAttachments().then((response) => {
        this.attachments = response.data.results
      })
    },
    downloadAttachment(attachmentDownloadURL) {
      api.getDownloadToken().then((response) => {
        const token = response.data["token"]
        location.assign(`${attachmentDownloadURL}?token=${token}`)
      })
    },
    getDataChart: function () {
      let records = {
        yearList: [],
        co2List: [],
      }

      api
        .getC02Global()
        .then((response) => {
          _.reverse(response.data.results).forEach((record) => {
            records.yearList.push(record.year)
            records.co2List.push(record.co2)
          })
        })
        .finally(() => {
          this.drawDataChart(records)
        })
    },
    drawDataChart(records) {
      this.co2GlobalRecords = {
        labels: records.yearList,
        datasets: [
          {
            label: "CO2 - Toneladas",
            data: records.co2List,
            fill: false,
            pointRadius: 5,
            backgroundColor: "#99cc32",
            borderColor: "#99cc32",
          },
        ],
      }
    },
  },
}
</script>
